import { Box, Tooltip, Typography } from '@mui/material';
import { TournamentBracketParticipantResultEntity } from '@okletsplay/core';
import { Identifier } from 'app/interfaces';
import { TournamentTeamEntity } from 'app/interfaces/entity';
import classNames from 'classnames';
import { BracketTeamResult } from 'modules/bracket/components/BracketTeamResult';
import { EmptyBracketTeamCard } from 'modules/bracket/components/EmptyBracketTeamCard';
import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { currentTournamentSelector } from 'modules/tournaments/store/current-tournament/selectors';
import NoAccountsIcon from '@mui/icons-material/NoAccounts';
import { TeamIcon } from 'shared/components/TeamIcon';
import { BracketSeedTeamStatus } from '../BracketSeedTeamStatus';
import { useStyles } from './BracketTeamCard.styles';

interface BracketTeamCardProps {
  team?: TournamentTeamEntity;
  matchResults: Record<Identifier, TournamentBracketParticipantResultEntity[]>;
  isEmptyMatch?: boolean;
  isReady?: boolean;
  isJoinGameTimerExpired?: boolean;
  isDisqualifiedTeam?: boolean;
}

export const BracketTeamCard: FC<BracketTeamCardProps> = ({
  team,
  matchResults,
  isEmptyMatch,
  isReady,
  isJoinGameTimerExpired,
  isDisqualifiedTeam,
}) => {
  const { classes } = useStyles();
  const isUnlimitedJoinTimer = useSelector(currentTournamentSelector.isUnlimitedJoinTimer);

  const isTeamStatusPresent = useMemo(() => {
    if (isUnlimitedJoinTimer) {
      return false;
    }

    return isReady || (isJoinGameTimerExpired && !isReady);
  }, [isUnlimitedJoinTimer, isJoinGameTimerExpired, isReady]);

  if (isEmptyMatch) {
    return <Typography>-</Typography>;
  }

  if (!team) {
    return <EmptyBracketTeamCard />;
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.teamContainer}>
        <TeamIcon size={32} mr={1} team={team} />
        <Tooltip title={team.name}>
          <Typography
            className={classNames(classes.text, { [classes.disqualified]: isDisqualifiedTeam })}
          >
            {team.name}
          </Typography>
        </Tooltip>
        {isDisqualifiedTeam && (
          <Tooltip title="Disqualified">
            <NoAccountsIcon />
          </Tooltip>
        )}
        {isTeamStatusPresent && <BracketSeedTeamStatus isReady={isReady} />}
      </Box>
      <BracketTeamResult matchResults={matchResults} teamId={team.id} />
    </Box>
  );
};
